import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WorkApplicationFormComponent } from '../work-application-form/work-application-form.component';
import { DynamicFormComponent } from '../../dynamic-form/dynamic-form/dynamic-form.component';
import { ShowlistComponent } from '../../list/showlist/showlist.component';
import { FormDataService } from '../../../services/form-data.service';
import { environment } from '../../../services/global-environment';

interface ApprovalStage {
  stage: string;
  stageName: string;
  fullDepartmentName: string;
  status: 'pending' | 'approved' | 'rejected' | 'not-started';
  approver?: string;
  approvedDate?: string;
  comment?: string;
}

interface RequiredForm {
  id: string;
  name: string;
  shortName: string;
  status: 'not-required' | 'required-not-filled' | 'filled';
  required: boolean;
}

interface WorkApplication {
  id: string;
  applicationDate: string;
  requestDepartment: string;
  workLocation: string;
  workPeriodStart: string;
  workPeriodEnd: string;
  contractingUnit: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected' | 'completed';
  approvalProcess: ApprovalStage[];
  requiredForms: RequiredForm[];
  // 其他屬性...
}

@Component({
  selector: 'app-work-application',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, WorkApplicationFormComponent, DynamicFormComponent, ShowlistComponent],
  templateUrl: './work-application.component.html',
  styleUrl: './work-application.component.scss'
})
export class WorkApplicationComponent implements OnInit {
  // 公司和表單ID相關
  private companyid = environment.companyid;
  workApplicationFormId: string = '';

  // 視圖切換
  showApplicationForm: boolean = false;
  isEditMode: boolean = false;
  currentEditData: any = null;

  // 列表相關
  applications: WorkApplication[] = [];
  filteredApplications: WorkApplication[] = [];

  // 篩選條件
  dateFilterStart: string = '';
  dateFilterEnd: string = '';
  contractingUnitFilter: string = '';
  statusFilter: string = '';
  searchText: string = '';

  // 分頁相關
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;

  // 選項資料
  contractingUnits: string[] = ['工程部', '資訊部', '總務部', '生產部', '品質部'];

  // 動態表單彈窗相關
  showFormModal: boolean = false;
  currentFormId: string = '';
  currentFormName: string = '';
  errorMessage: string = '';

  constructor(
    private fb: FormBuilder,
    private formDataService: FormDataService
  ) {}

  ngOnInit(): void {
    // 根據公司ID設定表單ID
    this.setWorkApplicationFormId();

    this.loadApplications();
  }

  /**
   * 根據公司ID設定施工申請單表單ID
   */
  private setWorkApplicationFormId(): void {
    switch (this.companyid.toLowerCase()) {
      case 'jsai':
        this.workApplicationFormId = '6a8bed49-6f22-4977-9417-b2e2a43f1f6d';
        break;
      case 'eirgenix':
        this.workApplicationFormId = 'b05f45f4-8083-460e-9f18-f89bf23883ad';
        break;
      default:
        // 預設使用 JSAI 的表單ID
        this.workApplicationFormId = '6a8bed49-6f22-4977-9417-b2e2a43f1f6d';
        console.warn(`未設定公司 ${this.companyid} 的施工申請單表單ID，使用預設值`);
        break;
    }
    console.log(`設定施工申請單表單ID為: ${this.workApplicationFormId} (公司: ${this.companyid})`);
  }

  // 載入申請單列表（模擬數據）
  loadApplications(): void {
    // 這裡在實際專案中應該會從服務呼叫API
    this.applications = this.getMockApplications();
    this.applyFilters();
  }

  // 處理表單提交事件
  onFormSubmit(formData: any): void {
    console.log('收到表單資料:', formData);

    if (this.isEditMode) {
      // 更新現有申請單
      const index = this.applications.findIndex(app => app.id === this.currentEditData?.id);
      if (index !== -1) {
        this.applications[index] = {
          ...this.applications[index],
          applicationDate: `${formData.year}/${formData.month}/${formData.day}`,
          requestDepartment: formData.requestDepartment,
          workLocation: formData.workLocation,
          workPeriodStart: `${formData.startYear}/${formData.startMonth}/${formData.startDay}`,
          workPeriodEnd: `${formData.endYear}/${formData.endMonth}/${formData.endDay}`,
          contractingUnit: formData.contractingUnit,
          status: 'pending'
        };

        alert('申請單更新成功！');
      }
    } else {
      // 創建新申請單
      const newId = 'WA' + (10000 + this.applications.length + 1).toString();
      const newApplication: WorkApplication = {
        id: newId,
        applicationDate: `${formData.year}/${formData.month}/${formData.day}`,
        requestDepartment: formData.requestDepartment,
        workLocation: formData.workLocation,
        workPeriodStart: `${formData.startYear}/${formData.startMonth}/${formData.startDay}`,
        workPeriodEnd: `${formData.endYear}/${formData.endMonth}/${formData.endDay}`,
        contractingUnit: formData.contractingUnit,
        status: 'pending',
        approvalProcess: this.updateApprovalProcessByStatus('pending'),
        requiredForms: this.generateRequiredForms() // 生成表單狀態
      };

      this.applications.unshift(newApplication);
      alert('申請單提交成功！');
    }

    // 返回列表視圖
    this.backToList();
    this.applyFilters();
  }

  // 處理表單取消事件
  onFormCancel(): void {
    this.backToList();
  }

  // 生成預設審核流程
  createDefaultApprovalProcess(): ApprovalStage[] {
    return [
      {
        stage: '承',
        stageName: '承',
        fullDepartmentName: '承辦單位',
        status: 'not-started'
      },
      {
        stage: '廠',
        stageName: '廠',
        fullDepartmentName: '廠務部',
        status: 'not-started'
      },
      {
        stage: '環',
        stageName: '環',
        fullDepartmentName: '環境保護處',
        status: 'not-started'
      },
      {
        stage: '請',
        stageName: '請',
        fullDepartmentName: '請購部門',
        status: 'not-started'
      },
      {
        stage: '環',
        stageName: '環',
        fullDepartmentName: '環境安全衛生處',
        status: 'not-started'
      }
    ];
  }

  // 根據申請單狀態更新審核流程
  updateApprovalProcessByStatus(status: string): ApprovalStage[] {
    const process = this.createDefaultApprovalProcess();

    switch (status) {
      case 'draft':
        // 草稿狀態，所有階段都是 not-started
        break;
      case 'pending':
        // 隨機設定一些階段為已完成，模擬審核進度
        const completedStages = Math.floor(Math.random() * 3) + 1;
        for (let i = 0; i < completedStages && i < process.length; i++) {
          process[i].status = 'approved';
          process[i].approver = '系統管理員';
          process[i].approvedDate = new Date().toLocaleDateString('zh-TW');
        }
        if (completedStages < process.length) {
          process[completedStages].status = 'pending';
        }
        break;
      case 'approved':
        // 全部完成
        process.forEach(stage => {
          stage.status = 'approved';
          stage.approver = '系統管理員';
          stage.approvedDate = new Date().toLocaleDateString('zh-TW');
        });
        break;
      case 'rejected':
        // 隨機一個階段被駁回
        const rejectedStage = Math.floor(Math.random() * process.length);
        for (let i = 0; i < rejectedStage; i++) {
          process[i].status = 'approved';
          process[i].approver = '系統管理員';
          process[i].approvedDate = new Date().toLocaleDateString('zh-TW');
        }
        process[rejectedStage].status = 'rejected';
        process[rejectedStage].approver = '系統管理員';
        process[rejectedStage].approvedDate = new Date().toLocaleDateString('zh-TW');
        process[rejectedStage].comment = '不符合安全規範';
        break;
      case 'completed':
        // 全部完成
        process.forEach(stage => {
          stage.status = 'approved';
          stage.approver = '系統管理員';
          stage.approvedDate = new Date().toLocaleDateString('zh-TW');
        });
        break;
    }

    return process;
  }
  // 生成模擬數據
  getMockApplications(): WorkApplication[] {
    const mockData: WorkApplication[] = [];
    const statuses: ('draft' | 'pending' | 'approved' | 'rejected' | 'completed')[] =
      ['draft', 'pending', 'approved', 'rejected', 'completed'];

    for (let i = 1; i <= 35; i++) {
      const date = new Date();
      date.setDate(date.getDate() - Math.floor(Math.random() * 30));

      const startDate = new Date(date);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + Math.floor(Math.random() * 10) + 1);

      const status = statuses[Math.floor(Math.random() * statuses.length)];

      mockData.push({
        id: 'WA' + (10000 + i).toString(),
        applicationDate: date.toLocaleDateString('zh-TW'),
        requestDepartment: ['資訊部', '工程部', '總務部', '生產部'][Math.floor(Math.random() * 4)],
        workLocation: ['辦公大樓', '生產廠區', '倉儲區', '研發實驗室'][Math.floor(Math.random() * 4)],
        workPeriodStart: startDate.toLocaleDateString('zh-TW'),
        workPeriodEnd: endDate.toLocaleDateString('zh-TW'),
        contractingUnit: this.contractingUnits[Math.floor(Math.random() * this.contractingUnits.length)],
        status: status,
        approvalProcess: this.updateApprovalProcessByStatus(status),
        requiredForms: this.generateRequiredForms() // 生成表單狀態
      });
    }

    return mockData;
  }

  // 篩選功能
  applyFilters(): void {
    let filtered = this.applications;

    // 日期篩選
    if (this.dateFilterStart && this.dateFilterEnd) {
      const startDate = new Date(this.dateFilterStart);
      const endDate = new Date(this.dateFilterEnd);

      filtered = filtered.filter(app => {
        const appDate = this.parseDate(app.applicationDate);
        return appDate >= startDate && appDate <= endDate;
      });
    }

    // 承辦單位篩選
    if (this.contractingUnitFilter) {
      filtered = filtered.filter(app =>
        app.contractingUnit === this.contractingUnitFilter
      );
    }

    // 狀態篩選
    if (this.statusFilter) {
      filtered = filtered.filter(app =>
        app.status === this.statusFilter
      );
    }

    // 搜尋文字
    if (this.searchText) {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(app =>
        app.requestDepartment.toLowerCase().includes(searchLower) ||
        app.workLocation.toLowerCase().includes(searchLower) ||
        app.id.toLowerCase().includes(searchLower)
      );
    }

    this.filteredApplications = filtered;
    this.totalItems = filtered.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);

    // 如果當前頁碼超出總頁數，重置為第一頁
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = 1;
    }

    // 應用分頁
    this.applyPagination();
  }

  // 分頁相關方法
  applyPagination(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.filteredApplications = this.filteredApplications.slice(startIndex, endIndex);
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.applyFilters();
    }
  }

  getCurrentPageLastItem(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
  }

  getPageNumbers(): number[] {
    const pageNumbers: number[] = [];
    const maxPages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = startPage + maxPages - 1;

    if (endPage > this.totalPages) {
      endPage = this.totalPages;
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  }

  // 重置篩選條件
  resetFilters(): void {
    this.dateFilterStart = '';
    this.dateFilterEnd = '';
    this.contractingUnitFilter = '';
    this.statusFilter = '';
    this.searchText = '';
    this.currentPage = 1;
    this.applyFilters();
  }

  // 工具函數
  parseDate(dateStr: string): Date {
    const parts = dateStr.split('/');
    return new Date(+parts[0], +parts[1] - 1, +parts[2]);
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'draft': return '草稿';
      case 'pending': return '待審核';
      case 'approved': return '已核准';
      case 'rejected': return '已駁回';
      case 'completed': return '已完成';
      default: return '未知';
    }
  }

  // 權限檢查
  canEdit(application: WorkApplication): boolean {
    return application.status === 'draft' || application.status === 'rejected';
  }

  canDelete(application: WorkApplication): boolean {
    return application.status === 'draft';
  }

  canApprove(application: WorkApplication): boolean {
    return application.status === 'pending';
  }

  canReject(application: WorkApplication): boolean {
    return application.status === 'pending';
  }

  // 動作處理
  createNewApplication(): void {
    this.isEditMode = false;
    this.currentEditData = null;
    this.showApplicationForm = true;
  }

  viewApplication(application: WorkApplication): void {
    // 在實際應用中，這裡應該會從後端獲取完整的申請單資料
    this.isEditMode = false;
    this.currentEditData = this.prepareEditData(application);
    this.showApplicationForm = true;
  }

  editApplication(application: WorkApplication): void {
    this.isEditMode = true;
    this.currentEditData = this.prepareEditData(application);
    this.showApplicationForm = true;
  }

  prepareEditData(application: WorkApplication): any {
    // 解析日期並準備編輯資料
    const appDate = this.parseDate(application.applicationDate);
    const startDate = this.parseDate(application.workPeriodStart);
    const endDate = this.parseDate(application.workPeriodEnd);

    return {
      id: application.id,
      year: appDate.getFullYear(),
      month: appDate.getMonth() + 1,
      day: appDate.getDate(),
      requestDepartment: application.requestDepartment,
      workLocation: application.workLocation,
      startYear: startDate.getFullYear(),
      startMonth: startDate.getMonth() + 1,
      startDay: startDate.getDate(),
      endYear: endDate.getFullYear(),
      endMonth: endDate.getMonth() + 1,
      endDay: endDate.getDate(),
      contractingUnit: application.contractingUnit,
      // 其他欄位可以根據需要添加
    };
  }

  deleteApplication(application: WorkApplication): void {
    if (confirm(`確定要刪除申請單 ${application.id} 嗎？`)) {
      const index = this.applications.findIndex(app => app.id === application.id);
      if (index !== -1) {
        this.applications.splice(index, 1);
        this.applyFilters();
        alert('申請單已成功刪除！');
      }
    }
  }

  approveApplication(application: WorkApplication): void {
    const appToUpdate = this.applications.find(app => app.id === application.id);
    if (appToUpdate) {
      appToUpdate.status = 'approved';
      this.applyFilters();
      alert(`申請單 ${application.id} 已核准！`);
    }
  }

  rejectApplication(application: WorkApplication): void {
    const reason = prompt(`請輸入駁回申請單 ${application.id} 的原因：`, '');
    if (reason !== null) {
      const appToUpdate = this.applications.find(app => app.id === application.id);
      if (appToUpdate) {
        appToUpdate.status = 'rejected';
        this.applyFilters();
        alert(`申請單 ${application.id} 已駁回！`);
      }
    }
  }

  // 返回列表
  backToList(): void {
    this.showApplicationForm = false;
    this.currentEditData = null;
  }

  // 取得審核狀況顯示
  getApprovalStatusDisplay(application: WorkApplication): string {
    if (application.status === 'draft') {
      return '尚未送審';
    }

    const process = application.approvalProcess;
    let statusText = '';

    process.forEach((stage, index) => {
      if (stage.status === 'approved') {
        statusText += stage.stageName;
      } else if (stage.status === 'pending') {
        statusText += `<span class="pending-stage">${stage.stageName}</span>`;
      } else if (stage.status === 'rejected') {
        statusText += `<span class="rejected-stage">${stage.stageName}</span>`;
      } else {
        statusText += `<span class="not-started-stage">${stage.stageName}</span>`;
      }

      if (index < process.length - 1) {
        statusText += ' → ';
      }
    });

    return statusText;
  }

  // 取得 tooltip 內容
  getApprovalTooltipContent(application: WorkApplication): string {
    if (application.status === 'draft') {
      return '此申請單尚未提交審核';
    }

    let tooltipContent = '審核流程詳情：\n';

    application.approvalProcess.forEach((stage, index) => {
      const statusText = stage.status === 'approved' ? '✓ 已通過' :
                        stage.status === 'pending' ? '⏳ 審核中' :
                        stage.status === 'rejected' ? '✗ 已駁回' : '⭕ 待開始';

      tooltipContent += `${index + 1}. ${stage.fullDepartmentName} (${stage.stageName}) - ${statusText}`;

      if (stage.approver) {
        tooltipContent += ` [${stage.approver}]`;
      }
      if (stage.approvedDate) {
        tooltipContent += ` ${stage.approvedDate}`;
      }
      if (stage.comment) {
        tooltipContent += ` 備註：${stage.comment}`;
      }

      tooltipContent += '\n';
    });

    return tooltipContent;
  }

  // 取得單一階段的 tooltip 內容
  getSingleStageTooltip(stage: ApprovalStage): string {
    const statusText = stage.status === 'approved' ? '✓ 已通過' :
                      stage.status === 'pending' ? '⏳ 審核中' :
                      stage.status === 'rejected' ? '✗ 已駁回' : '⭕ 待開始';

    let tooltipContent = `${stage.fullDepartmentName}\n狀態：${statusText}`;

    if (stage.approver) {
      tooltipContent += `\n審核人員：${stage.approver}`;
    }
    if (stage.approvedDate) {
      tooltipContent += `\n審核日期：${stage.approvedDate}`;
    }
    if (stage.comment) {
      tooltipContent += `\n備註：${stage.comment}`;
    }

    return tooltipContent;
  }

  // 生成申請單需要的表單列表
  generateRequiredForms(): RequiredForm[] {
    // 模擬根據作業類型決定需要的表單
    const allForms: RequiredForm[] = [
      {
        id: '6d755ec1-5f87-4f66-b6f1-14f0e3167624',
        name: '工作場所環境及危害因素告知單',
        shortName: '告知',
        status: 'required-not-filled',
        required: true
      },
      {
        id: '125f6568-d76d-4886-8211-bf72345dddba',
        name: '動火作業-許可證',
        shortName: '動火',
        status: 'not-required',
        required: false
      },
      {
        id: '5513327b-df14-4eca-b347-ec67554538fc',
        name: '吊掛作業-許可證',
        shortName: '吊掛',
        status: 'not-required',
        required: false
      },
      {
        id: 'a22f011b-9155-4e8e-8744-871ef4ac5986',
        name: '局限空間作業-許可證',
        shortName: '局限',
        status: 'not-required',
        required: false
      },
      {
        id: '0e0d7aec-22d2-4bf0-899d-f90c9d14ea08',
        name: '有機溶劑作業-許可證',
        shortName: '溶劑',
        status: 'not-required',
        required: false
      },
      {
        id: 'eaa426ec-ff8b-4681-bcdb-b7fcd55e49b2',
        name: '高架作業-許可證',
        shortName: '高架',
        status: 'not-required',
        required: false
      },
      {
        id: '1f5b1ca4-776e-41b9-99ff-50a38e9ec1a5',
        name: '特殊作業-許可證',
        shortName: '特殊',
        status: 'not-required',
        required: false
      }
    ];

    // 隨機決定哪些特殊作業需要填寫（模擬真實情況）
    const randomForms = [...allForms];

    // 工作場所告知單永遠是必要的，可能是已填寫或未填寫
    randomForms[0].status = Math.random() > 0.3 ? 'filled' : 'required-not-filled';

    // 隨機設定其他表單狀態
    for (let i = 1; i < randomForms.length; i++) {
      const rand = Math.random();
      if (rand > 0.7) {
        // 30% 機率需要填寫
        randomForms[i].required = true;
        randomForms[i].status = Math.random() > 0.4 ? 'filled' : 'required-not-filled';
      } else {
        // 70% 機率不需要填寫
        randomForms[i].required = false;
        randomForms[i].status = 'not-required';
      }
    }

    return randomForms;
  }

  // 取得表單狀態的顯示文字
  getFormStatusText(status: string): string {
    switch (status) {
      case 'not-required': return '不需要';
      case 'required-not-filled': return '未填寫';
      case 'filled': return '已填寫';
      default: return '未知';
    }
  }

  // 取得表單狀態的CSS類別
  getFormStatusClass(status: string): string {
    switch (status) {
      case 'not-required': return 'form-status-not-required';
      case 'required-not-filled': return 'form-status-required-not-filled';
      case 'filled': return 'form-status-filled';
      default: return '';
    }
  }

  // 取得表單狀態的 tooltip 內容
  getFormTooltipContent(form: RequiredForm): string {
    const statusText = this.getFormStatusText(form.status);
    let tooltipContent = `${form.name}\n狀態：${statusText}`;

    if (form.status === 'required-not-filled') {
      tooltipContent += '\n\n點擊填寫此表單';
    }

    return tooltipContent;
  }

  // 處理表單填寫點擊事件
  onFormBlockClick(form: RequiredForm): void {
    // 只有未填寫的表單才可以點擊
    if (form.status === 'required-not-filled') {
      this.handleFormFill(form);
    }
  }

  // 處理表單填寫
  handleFormFill(form: RequiredForm): void {
    // 直接使用表單的名稱和 ID，因為現在使用的是真正的 GUID
    console.log('開啟表單:', form.name, 'ID:', form.id);

    // 呼叫與「所需資料」區塊相同的表單新增邏輯
    this.openFormDataDialog(form.name, form.id);
  }

  // 開啟表單資料對話框（整合動態表單元件）
  openFormDataDialog(formName: string, formId: string): void {
    console.log('開啟表單:', formName, 'ID:', formId);

    // 設置當前表單資訊
    this.currentFormId = formId;
    this.currentFormName = formName;

    // 開啟動態表單彈窗
    this.showFormModal = true;
    this.errorMessage = '';
  }

  // 關閉表單彈窗
  closeFormModal(): void {
    this.showFormModal = false;
    this.currentFormId = '';
    this.currentFormName = '';
    this.errorMessage = '';
  }

  // 處理表單提交成功
  onFormSubmitSuccess(): void {
    // 更新對應的表單狀態為已填寫
    if (this.currentFormId) {
      this.updateFormStatus(this.currentFormId, 'filled');
    }

    // 關閉彈窗
    this.closeFormModal();

    // 顯示成功訊息
    alert(`表單「${this.currentFormName}」填寫完成！`);
  }

  // 鍵盤事件處理 - ESC 關閉彈出視窗
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.showFormModal) {
      this.closeFormModal();
      event.preventDefault();
    }
  }

  // 更新指定申請單的表單狀態
  updateFormStatus(formId: string, newStatus: 'not-required' | 'required-not-filled' | 'filled'): void {
    this.applications.forEach(application => {
      const form = application.requiredForms.find(f => f.id === formId);
      if (form) {
        form.status = newStatus;
      }
    });

    // 重新套用篩選以更新顯示
    this.applyFilters();
  }
}
